/**
 * Friendly names for dashboard pages + per-organisation page lists.
 *
 * Structure (which pages exist, which orgs mount them by code, and which API
 * endpoints each page calls) is GENERATED into `src/access/pageCatalogue.json`
 * by `scripts/access/genPageCatalogue.mjs` from `router.tsx` and the view
 * source. It runs on `npm start` and is checked on `npm run build`, so it
 * cannot drift from the router.
 *
 * Only the human-facing bits live here: a friendly name, a sidebar-style
 * section, and an optional caveat per path. Names come from the sidebar
 * labels in `src/helpers/sidebarDisplay.tsx` where one exists.
 *
 * Working assumption (agreed): page access implies access to the endpoints
 * that page uses, so the catalogue's endpoint list is what an allow/block
 * override on the Users page grants or removes.
 */
import { PAGE_CATALOGUE, isBaselinePage, pageEndpoints } from './pageCatalogue'

export { PROTECTED_PAGES, exclusiveEndpoints, isBaselinePage } from './pageCatalogue'

export interface OrganisationPage {
    path: string
    name: string
    section: string
    note?: string
    endpoints: string[]
    /** True when the org gets this page from the code baseline (router.tsx). */
    baseline: boolean
}

const NOT_IN_SIDEBAR = 'Reachable by URL only — not linked from the sidebar'
const DETAIL = 'Detail page — opened from its parent list'
const ADMIN_ONLY = 'Sentinel admins only (isAdminUser)'
const SCOPED = 'Scoped server-side: team leaders see their team; plain agents see a 403 message'

/** Display order for sections on the Users page card. */
export const SECTION_ORDER = [
    'Account',
    'General',
    'Sentinel Claims',
    'PCP Introducers',
    'Introducer Stats',
    'PCP Referral',
    'Introducer APIs',
    'Comms Management',
    'Funding Data',
    'Military Hearing Loss',
    'Re-Engagement',
    'Insurance Audit',
    'Sentinel Tools',
    'Admin',
]

interface PageMeta {
    name: string
    section: string
    note?: string
}

/** Friendly metadata keyed by route path. Paths missing here fall back to a humanised path. */
export const PAGE_META: Record<string, PageMeta> = {
    '/profile': { name: 'My Profile', section: 'Account', note: 'Self-service profile, MFA, sessions' },

    // General
    '/home': { name: 'Dashboard', section: 'General' },
    '/client-search': { name: 'Client Search', section: 'General' },
    '/chatbot': { name: 'Chatbot', section: 'General', note: NOT_IN_SIDEBAR },
    '/knowledge-base': { name: 'Knowledge Base', section: 'General', note: NOT_IN_SIDEBAR },

    // Sentinel Claims
    '/cases': { name: 'Cases', section: 'Sentinel Claims' },
    '/view-case/:url_case_ref': { name: 'Case Detail', section: 'Sentinel Claims', note: DETAIL },
    '/clients': { name: 'Clients', section: 'Sentinel Claims' },
    '/view-client/:url_client': { name: 'Client Detail', section: 'Sentinel Claims', note: DETAIL },
    '/media-claims': { name: 'Media Claims', section: 'Sentinel Claims' },
    '/media-claims/add-client': { name: 'Media Claims — Add Client', section: 'Sentinel Claims' },
    '/media-claims/view-client/:url_client': {
        name: 'Media Claims — Client Detail',
        section: 'Sentinel Claims',
        note: DETAIL,
    },
    '/manual-add-client': { name: 'Add Client', section: 'Sentinel Claims' },
    '/needs-sorting-clients': { name: 'Sort Manual Clients', section: 'Sentinel Claims' },
    '/loa-tracker': { name: 'LOA Tracker', section: 'Sentinel Claims' },
    '/bulk-actions': { name: 'Bulk Actions', section: 'Sentinel Claims', note: NOT_IN_SIDEBAR },
    '/introducers': { name: 'Introduced Cases', section: 'Sentinel Claims', note: NOT_IN_SIDEBAR },
    '/FOA': { name: 'FOA', section: 'Sentinel Claims', note: NOT_IN_SIDEBAR },
    '/foa-responses': { name: 'Lender FOA Responses', section: 'Sentinel Claims', note: NOT_IN_SIDEBAR },
    '/POC': { name: 'POC', section: 'Sentinel Claims', note: NOT_IN_SIDEBAR },
    '/FOS-imports': { name: 'FOS Imports', section: 'Sentinel Claims', note: NOT_IN_SIDEBAR },
    '/FOS-introducers': { name: 'FOS Introducers', section: 'Sentinel Claims', note: NOT_IN_SIDEBAR },
    '/FOS-transfers': { name: 'FOS Transfers', section: 'Sentinel Claims', note: NOT_IN_SIDEBAR },
    '/dsar-stats': { name: 'DSAR Stats', section: 'Sentinel Claims', note: NOT_IN_SIDEBAR },
    '/loc-stats': { name: 'LOC Stats', section: 'Sentinel Claims', note: NOT_IN_SIDEBAR },

    // PCP Introducers
    '/srds-home': { name: 'SRDS', section: 'PCP Introducers' },
    '/srds/client/:clientId': { name: 'SRDS Client', section: 'PCP Introducers', note: DETAIL },
    '/srds/claim/:claimId': { name: 'SRDS Claim', section: 'PCP Introducers', note: DETAIL },
    '/sentinel-home': { name: 'Sentinel Stats', section: 'PCP Introducers', note: 'Sidebar: Inactive group' },
    '/sentinel-introducer-stats': { name: 'Introducer Stats', section: 'PCP Introducers', note: NOT_IN_SIDEBAR },

    // Introducer stats (each also has its own org)
    '/adl-home': { name: 'ADL Stats', section: 'Introducer Stats' },
    '/adl-il-stats': { name: 'ADL IL Stats', section: 'Introducer Stats' },
    '/mail-referral-home': { name: 'Check Finance (Mail Referral) Stats', section: 'Introducer Stats' },
    '/mail-il-stats': { name: 'Mail IL Stats', section: 'Introducer Stats' },
    '/pcpmo26-home': { name: 'PCPMO26 Stats', section: 'Introducer Stats' },
    '/fairclaim-home': { name: 'Claim Delivery API Stats', section: 'Introducer Stats' },
    '/sentinel-matters-home': { name: 'SentinelMatters Stats', section: 'Introducer Stats' },
    '/mbfcheck-home': { name: 'MBFCheck Stats', section: 'Introducer Stats' },
    '/mfc-home': { name: 'Evans Hughes (MFC) Stats', section: 'Introducer Stats' },
    '/vision-launch-home': { name: 'Vision Launch Stats', section: 'Introducer Stats' },
    '/gorilla-home': { name: 'Gorilla Stats', section: 'Introducer Stats', note: NOT_IN_SIDEBAR },
    '/allegiant-home': { name: 'Allegiant Stats', section: 'Introducer Stats', note: NOT_IN_SIDEBAR },
    '/panaco-home': { name: 'Panaco Stats', section: 'Introducer Stats', note: NOT_IN_SIDEBAR },

    // PCP Referral
    '/pcp-referral/overview': { name: 'Overview', section: 'PCP Referral' },
    '/pcp-referral/engagement': { name: 'Engagement', section: 'PCP Referral' },
    '/pcp-referral/add-client': { name: 'Add Client', section: 'PCP Referral' },
    '/pcp-referral/engagement-stats': { name: 'Stats', section: 'PCP Referral' },
    '/pcp-referral/esign-history': { name: 'eSign History', section: 'PCP Referral' },
    '/pcp-referral/users': { name: 'Users & Teams', section: 'PCP Referral' },
    '/pcp-referral/team-stats': {
        name: 'My Team (Team Leader Stats)',
        section: 'PCP Referral',
        note: 'Engagement tier: only mounted for users flagged as a team leader',
    },

    // Introducer APIs
    '/fairclaim-api': { name: 'Claim Delivery API', section: 'Introducer APIs' },
    '/fairclaim-api/optics': {
        name: 'Claim Delivery API — Optics',
        section: 'Introducer APIs',
        note: 'Tab of /fairclaim-api',
    },
    '/fairclaim-api/linked-funding': {
        name: 'Claim Delivery API — Linked Funding',
        section: 'Introducer APIs',
        note: 'Tab of /fairclaim-api',
    },
    '/fairclaim-api/test-suite': {
        name: 'Claim Delivery API — Test Suite',
        section: 'Introducer APIs',
        note: 'Tab of /fairclaim-api',
    },
    '/fairclaim-api/keys': {
        name: 'Claim Delivery API — Keys',
        section: 'Introducer APIs',
        note: 'Tab of /fairclaim-api',
    },
    '/ffj-api': { name: 'FFJ API', section: 'Introducer APIs' },
    '/ffj-api/optics': { name: 'FFJ API — Optics', section: 'Introducer APIs', note: 'Tab of /ffj-api' },
    '/ffj-api/linked-funding': {
        name: 'FFJ API — Linked Funding',
        section: 'Introducer APIs',
        note: 'Tab of /ffj-api',
    },
    '/ffj-api/test-suite': { name: 'FFJ API — Test Suite', section: 'Introducer APIs', note: 'Tab of /ffj-api' },
    '/ffj-api/keys': { name: 'FFJ API — Keys', section: 'Introducer APIs', note: 'Tab of /ffj-api' },

    // Comms Management
    '/comms/search': { name: 'Search', section: 'Comms Management' },
    '/comms/mailboxes': { name: 'Mailboxes', section: 'Comms Management' },
    '/comms/mailboxes/:address': { name: 'Mailbox Workspace', section: 'Comms Management', note: DETAIL },
    '/comms/pipeline': { name: 'Pipeline', section: 'Comms Management' },
    '/comms/loc-batches': { name: 'Letters — LOC Batches', section: 'Comms Management' },
    '/comms/document-ingest': { name: 'Letters — Daily Post (incoming)', section: 'Comms Management' },
    '/comms/lenders': { name: 'Lenders', section: 'Comms Management' },
    '/comms/categories': { name: 'Categories & Rules', section: 'Comms Management' },
    '/comms/models': { name: 'AI Models', section: 'Comms Management' },
    '/comms/actions': { name: 'Agent Actions', section: 'Comms Management', note: NOT_IN_SIDEBAR },
    '/comms/inbox': { name: 'Inbox (redirects to Mailboxes)', section: 'Comms Management', note: 'Legacy redirect' },

    // Funding Data
    '/collect-funding-clients': { name: 'Collect Funding Clients', section: 'Funding Data' },
    '/allocate-claims': { name: 'Allocate Claims', section: 'Funding Data' },
    '/allocate-claims-full': { name: 'Allocate Claims (Full)', section: 'Funding Data', note: NOT_IN_SIDEBAR },
    '/ineligible-funding-clients': { name: 'Failed Audit', section: 'Funding Data' },
    '/introducer-funding-gap': { name: 'Non-fundable (Introducer Funding Gap)', section: 'Funding Data' },
    '/analyse-schema': { name: 'Analyse Schema', section: 'Funding Data' },
    '/analyse-schema/client/:clientKey': { name: 'Schema Client Detail', section: 'Funding Data', note: DETAIL },
    '/admin/approved-lenders': { name: 'Approved Lenders', section: 'Funding Data' },
    '/funding-batch/:batchId': { name: 'Funding Batch Details', section: 'Funding Data', note: DETAIL },
    '/funding-client/:clientId': { name: 'Funding Client Details', section: 'Funding Data', note: DETAIL },
    '/il-management': { name: 'IL Management', section: 'Funding Data', note: NOT_IN_SIDEBAR },
    '/il-add-clients': { name: 'IL Add Clients', section: 'Funding Data', note: NOT_IN_SIDEBAR },
    '/il-client/:id': { name: 'IL Client Detail', section: 'Funding Data', note: DETAIL },

    // Military Hearing Loss
    '/military-hearing-loss/lead-tracker': {
        name: 'Overview (Lead Tracker)',
        section: 'Military Hearing Loss',
        note: SCOPED,
    },
    '/military-hearing-loss/leads': { name: 'Clients', section: 'Military Hearing Loss' },
    '/military-hearing-loss/add-client': { name: 'Add Client', section: 'Military Hearing Loss' },
    '/military-hearing-loss/client/:clientId': {
        name: 'Client Detail',
        section: 'Military Hearing Loss',
        note: DETAIL,
    },
    '/military-hearing-loss/admin': { name: 'Agent Stats', section: 'Military Hearing Loss', note: SCOPED },
    '/military-hearing-loss/website-visitors': {
        name: 'Website Visitors',
        section: 'Military Hearing Loss',
        note: SCOPED,
    },
    '/military-hearing-loss/users': { name: 'Agents', section: 'Military Hearing Loss', note: SCOPED },

    // Re-Engagement
    '/re-engagement-overview': { name: 'Re-Engagement Overview', section: 'Re-Engagement' },
    '/re-engagement-search': { name: 'Re-Engagement Client Search', section: 'Re-Engagement' },

    // Insurance Audit
    '/insurance-audit': { name: 'Audit Cases', section: 'Insurance Audit', note: 'Read-only, whitelisted cases' },
    '/insurance-audit/client/:clientId': { name: 'Audit Client Detail', section: 'Insurance Audit', note: DETAIL },

    // Sentinel Tools
    '/omnibus-claims': { name: 'Omnibus Claims', section: 'Sentinel Tools' },
    '/omnibus-claims/:omnibusId': { name: 'Omnibus Group Detail', section: 'Sentinel Tools', note: DETAIL },
    '/viewthru-batch': { name: 'ViewThru Batch', section: 'Sentinel Tools' },
    '/viewthru-batch/analysis': {
        name: 'ViewThru — All Batches Analysis',
        section: 'Sentinel Tools',
        note: NOT_IN_SIDEBAR,
    },
    '/contracts': { name: 'Contracts', section: 'Sentinel Tools' },
    '/signature-review': { name: 'Signature Review', section: 'Sentinel Tools' },
    '/introducers-directory': { name: 'Introducers Directory', section: 'Sentinel Tools' },
    '/cancellation-readiness': { name: 'Cancellation Readiness', section: 'Sentinel Tools' },
    '/staff-email-signature': { name: 'Staff Email Signature', section: 'Sentinel Tools' },
    '/admin/credit-scrapers': { name: 'Credit Scrapers', section: 'Sentinel Tools' },
    '/claim-valuations': { name: 'Claim Valuations', section: 'Sentinel Tools' },
    '/report-links': { name: 'Report Builder', section: 'Sentinel Tools' },

    // Admin
    '/users': { name: 'Users', section: 'Admin', note: ADMIN_ONLY },
    '/admin/user-activity': { name: 'User Activity', section: 'Admin', note: ADMIN_ONLY },
    '/admin/insurance-audit': { name: 'Insurance Audit Admin', section: 'Admin', note: ADMIN_ONLY },
    '/admin/batch-visibility': { name: 'Omnibus Batch Visibility', section: 'Admin', note: ADMIN_ONLY },
    '/admin/omnibus-comms-upload': { name: 'Omnibus Comms Upload', section: 'Admin', note: ADMIN_ONLY },
    '/admin/document-access': { name: 'Bundle Access Log', section: 'Admin' },
    '/admin/document-access/keys': { name: 'Bundle Access — Keys', section: 'Admin' },
    '/admin/document-access/batch/:batchGroup': { name: 'Bundle Access — Batch', section: 'Admin', note: DETAIL },
    '/admin/document-access/access/:accessId': { name: 'Bundle Access — Detail', section: 'Admin', note: DETAIL },
    '/admin/style-guide': { name: 'Style Guide', section: 'Admin' },
}

/** Orgs that exist in the backend allow-list but mount no dashboard pages. */
const PAGELESS_ORGANISATIONS = ['Gorilla', 'Allegiant', 'Panaco', 'Funder Audit']

/** Explanatory note shown above the table for orgs that need context. */
export const ORGANISATION_PAGE_NOTES: Record<string, string> = {
    Sentinel:
        'Sentinel staff can view every page in the dashboard. Pages marked "Sentinel admins only" additionally require the Cognito admin flag. Overrides cannot be applied to Sentinel.',
    'Funder Audit':
        'Funder Audit users use the separate Funder Audit portal (funder-audit.sentinellegal.co.uk), not this dashboard. Their backend access is read-only funding batch data; this dashboard only exposes the common account pages to them.',
    Gorilla:
        'The Gorilla routes are currently commented out in the dashboard router, so this org only has the common account pages.',
    Allegiant:
        'The Allegiant routes are currently commented out in the dashboard router, so this org only has the common account pages.',
    Panaco: 'The Panaco routes are currently commented out in the dashboard router, so this org only has the common account pages.',
    'PCP Referral Engagement':
        'Engagement-tier agents. The "My Team" page is only mounted for users flagged as a team leader on their team membership.',
    'Military Hearing Loss':
        'Agent tier. All MHL routes are mounted, but stats/overview/agents pages are scoped server-side — plain agents see a 403 message, team leaders see their own team.',
    'Military Hearing Loss Admin': 'Admin tier — sees every MHL page without team scoping.',
    'Insurance Audit': 'External insurance auditors. Read-only access limited to whitelisted cases.',
    Score: 'External funding partner. Funding-batch allocation and analysis pages only.',
}

const humanise = (path: string) =>
    path
        .replace(/^\//, '')
        .replace(/\/:[^/]+/g, '')
        .split(/[/-]/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')

export const pageMeta = (path: string): PageMeta => PAGE_META[path] || { name: humanise(path), section: 'Other' }

const sectionRank = (section: string) => {
    const i = SECTION_ORDER.indexOf(section)
    return i === -1 ? SECTION_ORDER.length : i
}

export const sortPages = <T extends { section: string; name: string }>(pages: T[]): T[] =>
    [...pages].sort((a, b) => sectionRank(a.section) - sectionRank(b.section) || a.name.localeCompare(b.name))

/** Build a display row for a path (optionally overriding the baseline flag). */
export const toOrganisationPage = (path: string, baseline: boolean): OrganisationPage => {
    const meta = pageMeta(path)
    return { path, ...meta, endpoints: pageEndpoints(path), baseline }
}

/** Every page an org gets from the code baseline, in display order. */
export const getBaselinePages = (organisation: string): OrganisationPage[] =>
    sortPages(
        Object.keys(PAGE_CATALOGUE)
            .filter((path) => isBaselinePage(organisation, path))
            .map((path) => toOrganisationPage(path, true)),
    )

/** Every page in the catalogue, in display order (the Add page picker source). */
export const getAllPages = (): OrganisationPage[] =>
    sortPages(Object.keys(PAGE_CATALOGUE).map((path) => toOrganisationPage(path, false)))

/** True when we know this org (even if it mounts no pages). */
export const isKnownOrganisation = (organisation: string): boolean =>
    PAGELESS_ORGANISATIONS.includes(organisation) ||
    Object.values(PAGE_CATALOGUE).some((entry) => entry.baselineOrgs.includes(organisation))
